import React, { useContext, useEffect, useState } from "react";
import { useAppDispatch } from "store/hooks";
import * as FP from "utils/fp-js";
import { showError } from "store/reducers/errorReducer";
import { API } from "aws-amplify";
import {
  Button,
  InputLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DialogWindow } from "UI/DialogWindow";
import { FormTextField } from "UI/FormTextField";
import { FormSelectField } from "UI/FormSelectField";
import {
  emailValidation,
  phoneNumberValidation,
  nameValidation,
  notRequiredPhoneNumberValidation,
} from "models/validations";
import { GlobalContext } from "store/globalContext";
import { useCreateApiCall } from "hooks/useCreateApiCall";
import { FormMaskedTextField } from "UI/FormMaskedTextField";
import { EditActionButtons } from "UI/EditActionButtons";
import { useEditItem } from "hooks/useEditItem";
import { AddNewRegionDialog } from "./AddNewRegionDialog";
import { FormCheckbox } from "UI/FormCheckbox";

/** @type {import("types/commonTypes").Specialist}*/
const defaultFormData = {
  id: "",
  role: "",
  organizationId: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  jobTitle: "",
  marketingRegionId: "",
  smsIsEnabled: false,
  emailIsEnabled: false,
};

export const EditSpecialistDialog = ({
  title,
  role,
  specialistId,
  handleClose,
  refreshList,
  regionsOptions = {},
  refreshRegionsOptions = () => null,
}) => {
  const { createApiCall } = useCreateApiCall();
  const dispatch = useAppDispatch();
  const { showWarning, openConfirmationWithCallback, userData } =
    useContext(GlobalContext);
  const [isAddNewRegionDialogOpen, setIsAddNewRegionDialogOpen] =
    useState(false);

  const {
    previousData,
    setPreviousData,
    control,
    getValues,
    handleSubmit,
    isDirty,
    reset,
    handleCancel,
    watch,
  } = useEditItem({ defaultFormData, handleClose });

  /** @type {import("types/commonTypes").Organization[]} */
  const initialState = [];
  const [recipients, setRecipients] = useState(initialState);
  const [creatorRecipientId, setCreatorRecipientId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const deleteSpecialist = createApiCall(async () => {
    const specialist = getValues();
    const body = {
      specialist,
    };

    const result = await API.del("restApi", "/specialists", {
      body,
    });

    if (result?.error) {
      console.error(result.error);
      dispatch(showError(result.error));
      return;
    }

    showWarning({
      title: (
        <Stack justifyContent={"center"} alignItems={"center"} gap={3}>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            {role === "Originator" ? "Sender" : role} deleted successfully
          </Typography>
        </Stack>
      ),
      content: <></>,
    });

    refreshList();
    handleClose();
  });

  const handleDelete = () => {
    switch (role) {
      case "Originator":
        openConfirmationWithCallback({
          title: "Warning",
          message: "Deleting a sender cannot be undone. Proceed?",
          callbackFn: () => {
            deleteSpecialist();
          },
          buttonLabels: ["No", "Yes"],
        });
        break;
      case "Subsender":
        openConfirmationWithCallback({
          title: "Warning",
          message: "Deleting a subsender cannot be undone. Proceed?",
          callbackFn: () => {
            deleteSpecialist();
          },
          buttonLabels: ["No", "Yes"],
        });
        break;
      default:
        openConfirmationWithCallback({
          title: "Warning",
          message: `This ${
            role === "Originator" ? "Sender" : role
          } will be deleted from your organization. Proceed?`,
          callbackFn: () => {
            deleteSpecialist();
          },
          buttonLabels: ["No", "Yes"],
        });
    }
  };

  const onSubmit = createApiCall(async () => {
    const specialist = getValues();
    const body = {
      specialist,
    };

    const result = await API.post("restApi", "/specialists", {
      body,
    });

    if (result?.error) {
      console.error(result.error);
      dispatch(showError(result.error));
      return;
    }

    showWarning({
      title: (
        <Stack justifyContent={"center"} alignItems={"center"} gap={3}>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            {role === "Originator" ? "Sender" : role} updated successfully
          </Typography>
        </Stack>
      ),
      content: <></>,
    });

    refreshList();
    handleClose();
  });

  useEffect(() => {
    const getData = async () => {
      try {
        const result = await API.get("restApi", "/organizations", {});
        setRecipients(result?.Items ?? []);
      } catch (error) {}
    };

    getData();
  }, []);

  useEffect(() => {
    const loadData = createApiCall(async () => {
      const result = await API.get(
        "restApi",
        `/specialists/${specialistId}`,
        {}
      );
      const data = result?.Item ?? defaultFormData;
      setCreatorRecipientId(result?.creatorRecipientId);
      reset(data);
      setPreviousData(data);
      setIsLoading(false);
    });

    if (specialistId) {
      loadData();
    } else {
      reset(defaultFormData);
      setIsLoading(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specialistId]);

  recipients.sort((a, b) => (a.name > b.name ? 1 : -1));

  const recipientsOptions = recipients.reduce(
    (acc, { id, name, speciality, phone }) => ({
      ...acc,
      [id]: [name, speciality, phone].filter(Boolean).join(", "),
    }),
    {}
  );

  const specialistRole = watch("role");
  const specialistOrganizationId = watch("organizationId");

  const isOriginator =
    specialistRole === "Originator" || specialistRole === "Subsender";
  const isUser = role === "Marketer" || role === "Scheduler";

  const isSuperAdmin =
    userData?.role === "Super Admin" ||
    userData?.email === "admin@referral-stream.com";

  const isCreatorWrong = userData?.organizationId !== creatorRecipientId;
  const isAnotherOrganization =
    userData?.organizationId !== specialistOrganizationId;

  const isEditingDisabled =
    isLoading ||
    (isOriginator && !isSuperAdmin && isCreatorWrong && isAnotherOrganization);

  const isDeleteButtonPresent =
    (isOriginator && userData?.role === "Super Admin") ||
    (isUser && userData?.role === "Admin") ||
    (isOriginator && userData?.role === "Admin" && !isEditingDisabled);

  return (
    <DialogWindow
      open={!!specialistId}
      withCloseButton
      handleClose={handleCancel}
      title={`${isEditingDisabled ? "View" : "Edit"} ${title}`}
      content={
        <>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack gap={1}>
              <Stack direction={"row"} justifyContent={"space-between"} gap={2}>
                {FP.match(role)
                  .on(
                    "Originator",
                    <Stack alignItems={"flex-start"} flex={1}>
                      <InputLabel htmlFor="organizationId">Practice</InputLabel>
                      <TextField
                        id="organizationId"
                        name="organizationId"
                        value={recipientsOptions[getValues("organizationId")]}
                        fullWidth
                        disabled
                      />
                    </Stack>
                  )
                  .otherwise(null)}
              </Stack>
              <Stack direction={"row"} gap={1}>
                <Stack alignItems={"flex-start"}>
                  <InputLabel htmlFor="firstName">First name</InputLabel>
                  <FormTextField
                    id="firstName"
                    name="firstName"
                    control={control}
                    rules={nameValidation}
                    InputProps={{
                      autoComplete: "off",
                    }}
                    disabled={isEditingDisabled}
                  />
                </Stack>
                <Stack alignItems={"flex-start"}>
                  <InputLabel htmlFor="lastName">Last name</InputLabel>
                  <FormTextField
                    id="lastName"
                    name="lastName"
                    control={control}
                    rules={nameValidation}
                    InputProps={{
                      autoComplete: "off",
                    }}
                    disabled={isEditingDisabled}
                  />
                </Stack>
              </Stack>
              <Stack alignItems={"flex-start"}>
                <InputLabel htmlFor="phone">Phone</InputLabel>
                <FormMaskedTextField
                  id="phone"
                  name="phone"
                  control={control}
                  rules={
                    specialistRole === "Originator"
                      ? notRequiredPhoneNumberValidation
                      : phoneNumberValidation
                  }
                  mask="999-999-9999"
                  InputProps={{
                    autoComplete: "off",
                  }}
                  disabled={isEditingDisabled}
                />
              </Stack>
              <Stack alignItems={"flex-start"}>
                <InputLabel htmlFor="email">
                  Email for Referral Stream notifications
                </InputLabel>
                <FormTextField
                  id="email"
                  name="email"
                  control={control}
                  rules={emailValidation}
                  InputProps={{
                    autoComplete: "off",
                  }}
                  disabled={isEditingDisabled}
                />
              </Stack>
              {FP.match(role)
                .on(
                  ["Originator", "Subsender"],
                  <Stack alignItems={"flex-start"}>
                    <InputLabel htmlFor="jobTitle">Job Title</InputLabel>
                    <FormTextField
                      id="jobTitle"
                      name="jobTitle"
                      control={control}
                      InputProps={{
                        autoComplete: "off",
                      }}
                      disabled={isEditingDisabled}
                    />
                  </Stack>
                )
                /* TODO - add marketing region list functionality */
                .on(
                  "Marketer",
                  <Stack direction={"row"} gap={2} alignItems={"flex-end"}>
                    <Stack alignItems={"flex-start"} flex="1">
                      <InputLabel htmlFor="marketingRegionId">
                        Marketing Region
                      </InputLabel>
                      <FormSelectField
                        id="marketingRegionId"
                        name="marketingRegionId"
                        control={control}
                        options={regionsOptions}
                        disabled={isEditingDisabled}
                      />
                    </Stack>
                    <Button onClick={() => setIsAddNewRegionDialogOpen(true)}>
                      Add new region
                    </Button>
                  </Stack>
                )
                .otherwise(null)}
              {["Marketer", "Scheduler", "Originator", "Subsender"].includes(
                role
              ) && (
                <Stack
                  direction={"row"}
                  justifyContent={"space-between"}
                  alignItems={"center"}
                  gap={1}
                  flexWrap={"wrap"}
                >
                  Notification configuration:
                  <Stack direction={"row"} alignItems={"flex-start"} gap={1}>
                    <Stack alignItems={"flex-start"}>
                      <FormCheckbox
                        name="smsIsEnabled"
                        control={control}
                        label={"SMS enabled"}
                      />
                    </Stack>
                    <Stack alignItems={"flex-start"}>
                      <FormCheckbox
                        name="emailIsEnabled"
                        control={control}
                        label={"Email enabled"}
                      />
                    </Stack>
                  </Stack>
                </Stack>
              )}
            </Stack>
          </form>
          <AddNewRegionDialog
            open={isAddNewRegionDialogOpen}
            handleClose={() => setIsAddNewRegionDialogOpen(false)}
            refreshList={refreshRegionsOptions}
          />
        </>
      }
      actions={
        !isEditingDisabled && (
          <EditActionButtons
            handleCancel={handleCancel}
            handleDelete={handleDelete}
            isDeleteButtonPresent={isDeleteButtonPresent}
            previousData={previousData}
            isDirty={isDirty}
            reset={reset}
            saveChanges={handleSubmit(onSubmit)}
          />
        )
      }
    />
  );
};
